import React, { useState, useEffect } from 'react';
import { losApi } from '../../losApi';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { CircleCheck, Circle, Trash2, Plus, BellRing, Pencil, Sparkles } from 'lucide-react';
import { ModuleHeader, Fld, SelectFld } from '../shared/formFields';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { useStaff, displayName } from '../../kit/OwnerChip';
import { formatDueDate, dueState, fromApi, isDateOnly } from '../../kit/time';

export function ConditionsTab({ loan, onChange }) {
  const [conds, setConds] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // the condition open in the edit dialog
  const [sugs, setSugs] = useState([]); // AI suggestions waiting on a person (ticket 192, D22)
  const [editingSug, setEditingSug] = useState(null); // the suggestion open in "Edit and accept"
  const staff = useStaff();
  const [paused, setPaused] = useState(!!loan.reminders_paused);
  const [savingPause, setSavingPause] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const load = async () => {
    try { setConds(await losApi.listConditions(loan.loan_file_id)); setLoadFailed(false); }
    catch { setLoadFailed(true); }
  };
  const loadSugs = () => losApi.listSuggestions(loan.loan_file_id).then(setSugs).catch(() => {});
  useEffect(() => { load(); loadSugs(); }, [loan.loan_file_id]);
  useEffect(() => { setPaused(!!loan.reminders_paused); }, [loan.reminders_paused]);

  // Ticket 132: stops the daily condition reminders (borrower + LO) for this deal.
  const togglePause = async (next) => {
    setPaused(next);
    setSavingPause(true);
    try {
      await losApi.updateLoan(loan.loan_file_id, { reminders_paused: next });
      onChange?.();
    } catch (e) {
      setPaused(!next);
      toast({ title: 'Could not change reminders', description: errorMessage(e) });
    } finally {
      setSavingPause(false);
    }
  };

  const setStatus = async (cid, status) => {
    try { await losApi.updateCondition(cid, { status }); load(); }
    catch (e) { toast({ title: 'Could not update the condition', description: errorMessage(e) }); }
  };
  const del = async (cid) => {
    if (!(await confirmDialog({ title: 'Delete this condition?', confirmText: 'Delete', destructive: true }))) return;
    try { await losApi.deleteCondition(cid); load(); }
    catch (e) { toast({ title: 'Could not delete the condition', description: errorMessage(e) }); }
  };

  const acceptSug = async (sug) => {
    try { await losApi.acceptSuggestion(sug.suggestion_id); toast({ title: 'Condition added' }); }
    catch (e) { toast({ title: 'Could not accept the suggestion', description: errorMessage(e) }); }
    loadSugs(); load();
  };
  const dismissSug = async (sug) => {
    try { await losApi.dismissSuggestion(sug.suggestion_id); }
    catch (e) { toast({ title: 'Could not dismiss the suggestion', description: errorMessage(e) }); }
    loadSugs();
  };
  const verifySug = async (sug) => {
    try { await losApi.clearSuggestionCitation(sug.suggestion_id); }
    catch (e) { toast({ title: 'Could not update the suggestion', description: errorMessage(e) }); }
    loadSugs();
  };

  const grouped = { prior_to_docs: [], prior_to_funding: [], at_closing: [], post_closing: [], info_only: [] };
  conds.forEach((c) => (grouped[c.category] = grouped[c.category] || []).push(c));

  return (
    <div className="space-y-4">
      <ModuleHeader
        title={`Conditions (${conds.length})`}
        subtitle={`${conds.filter(c => c.status === 'cleared').length} cleared · ${conds.filter(c => c.status === 'open').length} open`}
        extraButtons={<>
          <label className="text-[12px] text-slate-600 inline-flex items-center gap-2 mr-3 cursor-pointer" title="Stops the daily condition reminders to the borrower and the loan officer for this deal">
            <Switch checked={paused} disabled={savingPause} onCheckedChange={togglePause} data-testid="pause-reminders-toggle" aria-label="Pause reminders" />
            Pause reminders
          </label>
          <button onClick={() => setShowNew(true)} data-testid="add-condition-btn" className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#070B5F' }}><Plus size={12} /> Add Condition</button>
        </>}
      />
      {loadFailed && (
        <div className="bg-white rounded-xl border border-red-200 p-4 text-sm text-slate-600 flex items-center gap-3" role="alert" data-testid="conditions-load-error">
          {conds.length ? 'We could not refresh the conditions, so this list may be out of date.' : 'We could not load the conditions.'}
          <button onClick={load} className="font-semibold underline" data-testid="conditions-retry">Retry</button>
        </div>
      )}
      {sugs.length > 0 && (
        <SuggestedConditions sugs={sugs} staff={staff} onAccept={acceptSug} onEdit={setEditingSug} onDismiss={dismissSug} onVerify={verifySug} />
      )}
      {Object.entries(grouped).map(([cat, list]) => list.length > 0 && (
        <div key={cat} className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: '#6BA82E' }}>{cat.replace(/_/g, ' ')} ({list.length})</div>
          <div className="space-y-1">
            {list.map((c) => <ConditionRow key={c.condition_id} cond={c} staff={staff} onStatus={(s) => setStatus(c.condition_id, s)} onEdit={() => setEditing(c)} onDelete={() => del(c.condition_id)} />)}
          </div>
        </div>
      ))}
      {conds.length === 0 && !loadFailed && (
        <div className="bg-white rounded-xl p-10 border border-dashed border-slate-200 text-center text-sm text-slate-400 italic">
          No conditions yet. Add manually — or use the AI Processor to auto-generate a full condition list.
        </div>
      )}
      {showNew && <ConditionModal loanId={loan.loan_file_id} staff={staff} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editingSug && <ConditionModal suggestion={editingSug} staff={staff} onClose={() => setEditingSug(null)} onSaved={() => { setEditingSug(null); loadSugs(); load(); }} />}
      {editing && <ConditionModal cond={editing} staff={staff} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// Who a condition is waiting on: the borrower, or a teammate by user_id. Older rows may
// hold free text ("underwriter"); those show as written until someone edits them.
const UNASSIGNED = '__none';
const assigneeLabel = (value, staff) => {
  if (!value) return 'unassigned';
  if (value === 'borrower') return 'borrower';
  const m = staff.find((x) => x.user_id === value);
  return m ? displayName(m) : value;
};

function ConditionRow({ cond, staff, onStatus, onEdit, onDelete }) {
  const open = cond.status === 'open' || cond.status === 'submitted';
  const statusColors = { open: 'text-amber-600', submitted: 'text-blue-600', cleared: 'text-emerald-600', waived: 'text-slate-500' };
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 group" data-testid={`condition-${cond.condition_id}`}>
      <button onClick={() => onStatus(cond.status === 'cleared' ? 'open' : 'cleared')} className={`${statusColors[cond.status]} shrink-0 mt-0.5`}>
        {cond.status === 'cleared' ? <CircleCheck size={18} /> : <Circle size={18} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-semibold ${cond.status === 'cleared' ? 'line-through text-slate-400' : ''}`} style={{ color: cond.status === 'cleared' ? undefined : '#030524' }}>{cond.title}</div>
        {cond.description && <div className="text-[11px] text-slate-500 mt-0.5">{cond.description}</div>}
        <div className="flex gap-2 mt-1.5 items-center flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-slate-400">→ {assigneeLabel(cond.assigned_to, staff)}</span>
          {cond.due_date && (
            <span
              className={`text-[10px] font-semibold ${open && dueState(cond.due_date) === 'overdue' ? 'text-red-600' : 'text-slate-500'}`}
              data-testid={`condition-due-${cond.condition_id}`}
            >
              Due {formatDueDate(cond.due_date)}{open && dueState(cond.due_date) === 'overdue' ? ' · overdue' : ''}
            </span>
          )}
          {(cond.source === 'ai_processor' || cond.created_by === 'ai_processor') && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#F3FAE8', color: '#4c7f21' }}>AI</span>}
          <ReminderBadge sent={cond.reminders_sent} condId={cond.condition_id} />
        </div>
      </div>
      <Select value={cond.status} onValueChange={onStatus}>
        <SelectTrigger className="h-7 w-[112px] text-[11px] px-2" aria-label="Status" data-testid={`condition-status-${cond.condition_id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="open">Open</SelectItem><SelectItem value="submitted">Submitted</SelectItem>
          <SelectItem value="cleared">Cleared</SelectItem><SelectItem value="waived">Waived</SelectItem>
        </SelectContent>
      </Select>
      <button onClick={onEdit} className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-slate-700 transition" aria-label="Edit condition" data-testid={`condition-edit-${cond.condition_id}`}><Pencil size={13} /></button>
      <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-red-500 transition" aria-label="Delete condition"><Trash2 size={13} /></button>
    </div>
  );
}

const CATEGORY_LABEL = { prior_to_docs: 'Prior to Docs', prior_to_funding: 'Prior to Funding', at_closing: 'At Closing', post_closing: 'Post Closing', info_only: 'Info Only' };

// Ticket 192, D22: what the AI Processor proposed. None of these is a condition yet, so
// reminders and counts ignore them; a person accepts, edits and accepts, or dismisses.
function SuggestedConditions({ sugs, staff, onAccept, onEdit, onDismiss, onVerify }) {
  return (
    <div className="rounded-xl border p-5" style={{ background: '#F7F5FF', borderColor: '#D9D2F7' }} data-testid="suggested-conditions">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest" style={{ color: '#5b3fc4' }}>
        <Sparkles size={12} /> Suggested conditions ({sugs.length})
      </div>
      <p className="text-[11px] text-slate-500 mt-1 mb-3">From the AI Processor. These are not conditions until you accept them, and they expire after 30 days.</p>
      <div className="space-y-2">
        {sugs.map((s) => (
          <div key={s.suggestion_id} className="bg-white rounded-lg border border-slate-200 p-3" data-testid={`suggestion-${s.suggestion_id}`}>
            <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>{s.title}</div>
            {s.description && <div className="text-[11px] text-slate-500 mt-0.5">{s.description}</div>}
            <div className="flex gap-2 mt-1.5 items-center flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">{CATEGORY_LABEL[s.category] || s.category}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400">→ {assigneeLabel(s.assigned_to, staff)}</span>
              {s.guideline_citation && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1.5"
                  style={s.citation_verified ? { background: '#F3FAE8', color: '#4c7f21' } : { background: '#FFF7E6', color: '#92600a' }}
                  data-testid={`suggestion-citation-${s.suggestion_id}`}>
                  {s.citation_verified ? 'Guideline' : 'AI-cited, unverified'}: {s.guideline_citation}
                  {!s.citation_verified && (
                    <button onClick={() => onVerify(s)} className="underline font-semibold" data-testid={`suggestion-verify-${s.suggestion_id}`}>Mark verified</button>
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-2 mt-2.5">
              <button onClick={() => onAccept(s)} data-testid={`suggestion-accept-${s.suggestion_id}`} className="text-[12px] font-semibold px-3 py-1 rounded-lg text-white" style={{ background: '#070B5F' }}>Accept</button>
              <button onClick={() => onEdit(s)} data-testid={`suggestion-edit-${s.suggestion_id}`} className="text-[12px] font-semibold px-3 py-1 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Edit and accept</button>
              <button onClick={() => onDismiss(s)} data-testid={`suggestion-dismiss-${s.suggestion_id}`} className="text-[12px] px-3 py-1 rounded-lg text-slate-500 hover:bg-slate-100">Dismiss</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const REMINDER_WHO = { borrower: 'borrower', lo: 'LO' };
const REMINDER_STAGE = { 't-2': 'due soon', t0: 'due today', escalation: 'escalated', repeat: 'overdue' };
// Mongo hands back naive UTC; read a zone-less timestamp as UTC.
const asUtc = (at) => (typeof at === 'string' && !/(Z|[+-]\d\d:?\d\d)$/.test(at) ? `${at}Z` : at);
const fmtReminderDate = (at) => new Date(asUtc(at)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function reminderLine(r) {
  const who = REMINDER_WHO[r.audience] || r.audience;
  const how = r.channel === 'skipped' ? `skipped (${r.reason || 'no channel'})` : `by ${r.channel}`;
  return `${fmtReminderDate(r.at)} · ${who} · ${REMINDER_STAGE[r.stage] || r.stage} · ${how}`;
}

// Ticket 132: the latest reminder the daily loop sent (or skipped) for this condition.
function ReminderBadge({ sent, condId }) {
  if (!sent || sent.length === 0) return null;
  const last = sent[sent.length - 1];
  const skipped = last.channel === 'skipped';
  const who = REMINDER_WHO[last.audience] || last.audience;
  return (
    <span
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
      style={skipped ? { background: '#F1F5F9', color: '#64748b' } : { background: '#EEF2FF', color: '#070B5F' }}
      title={sent.map(reminderLine).join('\n')}
      data-testid={`reminder-badge-${condId}`}
    >
      <BellRing size={10} />
      {skipped ? 'Reminder skipped' : `Reminded ${who}`} {fmtReminderDate(last.at)}
    </span>
  );
}

// Borrower, then the team. A value that is neither (older free text) stays selectable
// so opening and saving the dialog does not silently drop it.
function assigneeOptions(current, staff) {
  const opts = [[UNASSIGNED, 'Unassigned'], ['borrower', 'Borrower'], ...staff.map((m) => [m.user_id, displayName(m)])];
  if (current && !opts.some(([k]) => k === current)) opts.push([current, current]);
  return opts;
}

// The value for <input type="date">. A date-only due date is its UTC day; one with a
// real time is the viewer's local day (same rule as formatDueDate).
const dateInputValue = (iso) => {
  if (!iso) return '';
  const d = fromApi(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (isDateOnly(d)) return d.toISOString().slice(0, 10);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Add (no `cond`) or edit (`cond`) one condition, or accept a suggestion (`suggestion`) after
// editing it. Edit sends only what changed, so a due date with a real time is left alone
// unless the day itself is changed.
function ConditionModal({ loanId, cond, suggestion, staff, onClose, onSaved }) {
  const base = cond || suggestion;
  const [f, setF] = useState(base
    ? { title: base.title || '', description: base.description || '', category: base.category, assigned_to: base.assigned_to || '', due_date: dateInputValue(base.due_date) }
    : { title: '', description: '', category: 'prior_to_docs', assigned_to: 'borrower', due_date: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return;
    setSaving(true);
    try {
      if (suggestion) {
        const body = {};
        if (f.title.trim() !== suggestion.title) body.title = f.title.trim();
        if (f.description !== (suggestion.description || '')) body.description = f.description;
        if (f.category !== suggestion.category) body.category = f.category;
        if (f.assigned_to !== (suggestion.assigned_to || '')) body.assigned_to = f.assigned_to;
        await losApi.acceptSuggestion(suggestion.suggestion_id, body);
      } else if (cond) {
        const body = {};
        if (f.title.trim() !== cond.title) body.title = f.title.trim();
        if (f.description !== (cond.description || '')) body.description = f.description;
        if (f.category !== cond.category) body.category = f.category;
        if (f.assigned_to !== (cond.assigned_to || '')) body.assigned_to = f.assigned_to || null;
        if (f.due_date !== dateInputValue(cond.due_date)) body.due_date = f.due_date || null;
        if (Object.keys(body).length) await losApi.updateCondition(cond.condition_id, body);
      } else {
        await losApi.addCondition(loanId, { ...f, title: f.title.trim(), due_date: f.due_date || null });
      }
      onSaved();
    } catch (e) {
      toast({ title: suggestion ? 'Could not accept the suggestion' : cond ? 'Could not save the condition' : 'Could not add the condition', description: errorMessage(e) });
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle className="font-serif text-lg font-bold mb-4" style={{ color: '#030524' }}>{suggestion ? 'Edit and accept' : cond ? 'Edit Condition' : 'New Condition'}</DialogTitle>
        <Fld label="Title" value={f.title} onChange={(v) => setF(x => ({ ...x, title: v }))} testId="condition-title" />
        <div className="mt-3"><Label className="text-xs">Description</Label>
          <Textarea rows={3} value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1" data-testid="condition-description" /></div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <SelectFld label="Category" value={f.category} onChange={(v) => setF(x => ({ ...x, category: v }))}
            options={[['prior_to_docs', 'Prior to Docs'], ['prior_to_funding', 'Prior to Funding'], ['at_closing', 'At Closing'], ['post_closing', 'Post Closing'], ['info_only', 'Info Only']]} />
          <SelectFld label="Assigned to" value={f.assigned_to || UNASSIGNED} onChange={(v) => setF(x => ({ ...x, assigned_to: v === UNASSIGNED ? '' : v }))}
            options={assigneeOptions(f.assigned_to, staff)} testId="condition-assigned-to" />
        </div>
        {!suggestion && (
          <div className="mt-3 w-1/2 pr-1.5">
            <Fld label="Due date" type="date" value={f.due_date} onChange={(v) => setF(x => ({ ...x, due_date: v }))} testId="condition-due-date" />
          </div>
        )}
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving || !f.title.trim()} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: '#070B5F' }} data-testid="save-condition">{suggestion ? 'Accept' : 'Save'}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
